import { columns } from "@/components/news-notices/news-notices.columns";
import { DataTable } from "@/components/table/table";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useNewsNoticesModal } from "@/hooks/useNewsNoticesModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { ConfirmModal } from "../ui/confirm-modal";
import { NewsNoticesModalForm } from "./news-notices-modal-form";

const newsNoticeFormSchema = z.object({
  noticeType: z.enum(["news", "notice"]),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  author: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).default([]),
  priority: z.enum(["low", "normal", "high", "urgent"]).default("normal"),
  status: z.enum(["draft", "published", "archived"]).default("published"),
  date: z.date(),
  expirationDate: z.date().optional(),
  featuredImageId: z.string().optional(),
  attachmentId: z.string().optional(),
});

type NewsNoticeFormValues = z.infer<typeof newsNoticeFormSchema>;

export function NewsNoticesTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data: newsNotices = [], isLoading } = useQuery(
    trpc.newsNotices.list.queryOptions(),
  );

  const deleteId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);

  const isModalOpen = useNewsNoticesModal((s) => s.isOpen);
  const setIsModalOpen = useNewsNoticesModal((s) => s.setIsModalOpen);
  const setNewsNoticeId = useNewsNoticesModal((s) => s.setNewsNoticeId);
  const newsNoticeId = useNewsNoticesModal((s) => s.newsNoticeId);
  const defaultValues = useNewsNoticesModal((s) => s.defaultValues);
  const setDefaultValues = useNewsNoticesModal((s) => s.setDefaultValues);

  const form = useForm<NewsNoticeFormValues>({
    resolver: zodResolver(newsNoticeFormSchema),
    defaultValues: {
      noticeType: "news",
      title: "",
      description: "",
      author: "",
      category: "",
      tags: [],
      priority: "normal",
      status: "published",
      date: new Date(),
      expirationDate: undefined,
      featuredImageId: undefined,
      attachmentId: undefined,
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        noticeType: defaultValues.noticeType || "news",
        title: defaultValues.title || "",
        description: defaultValues.description || "",
        author: defaultValues.author || "",
        category: defaultValues.category || "",
        tags: defaultValues.tags || [],
        priority: defaultValues.priority || "normal",
        status: defaultValues.status || "published",
        date: defaultValues.date || new Date(),
        expirationDate: defaultValues.expirationDate,
        featuredImageId: defaultValues.featuredImageId,
        attachmentId: defaultValues.attachmentId,
      });
    }
  }, [form, defaultValues]);

  const deleteMutation = useMutation(
    trpc.newsNotices.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["newsNotices"] });
        setDeleteId(null);
        toast.success("News/Notice deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to delete");
      },
    }),
  );

  const createMutation = useMutation(
    trpc.newsNotices.create.mutationOptions({
      onSuccess: () => {
        form.reset();
        queryClient.invalidateQueries({ queryKey: ["newsNotices"] });
        setNewsNoticeId(null);
        toast.success("News/Notice created successfully");
        setIsModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create");
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.newsNotices.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["newsNotices"] });
        setNewsNoticeId(null);
        setIsModalOpen(false);
        form.reset();
        toast.success("News/Notice updated successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update");
      },
    }),
  );

  const handleSubmit = (data: NewsNoticeFormValues) => {
    if (newsNoticeId) {
      updateMutation.mutate({ ...data, id: newsNoticeId });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              News & Notices
            </h2>
            <p className="text-muted-foreground">
              Manage news articles and public notices for the website
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => {
                setDefaultValues({});
                form.reset({
                  noticeType: "news",
                  title: "",
                  description: "",
                  author: "",
                  category: "",
                  tags: [],
                  priority: "normal",
                  status: "published",
                  date: new Date(),
                  expirationDate: undefined,
                  featuredImageId: undefined,
                  attachmentId: undefined,
                });
                setIsModalOpen(true);
              }}
            >
              New Item
            </Button>
          </div>
        </div>

        <NewsNoticesModalForm
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        <ConfirmModal
          open={Boolean(deleteId)}
          onConfirm={() => {
            if (deleteId) deleteMutation.mutate({ id: deleteId });
          }}
          onDismiss={() => {
            setDeleteId(null);
          }}
          isLoading={deleteMutation.isPending}
        />

        {!isLoading && (
          <DataTable
            data={newsNotices}
            columns={columns}
            filterColumn="title"
          />
        )}
      </div>
    </div>
  );
}
